"use client";

// ============================================================
// CLUBHUB – Navigation auf dem Telefon (PWA)
// ============================================================
// Zwei Teile: der Burger oben links oeffnet ein Blatt mit ALLEN
// Bereichen; unten steht eine Leiste mit den vier haeufigsten plus
// „Mehr“. Unterhalb von lg sichtbar, die Leiste dort ersetzt.
// ============================================================
import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import * as Dialog from "@radix-ui/react-dialog";
import { Menu, MoreHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { NAV_FUSS, NAV_GRUPPEN, NAV_KONTO, NAVIGATION, istAktiv, type NavEintrag } from "@/lib/navigation";
import { ClubhubMark } from "./clubhub-mark";
import { ThemeSwitch } from "./theme-switch";
import type { ClubView, UserView } from "@/lib/views";

function Zeile({ e, aktiv }: { e: NavEintrag; aktiv: boolean }) {
  const Icon = e.icon;
  return (
    <Link
      href={e.href}
      aria-current={aktiv ? "page" : undefined}
      className={cn(
        "text-body-md flex items-center gap-3 rounded-lg px-3 py-2.5",
        aktiv ? "bg-bg-tertiary text-text-primary font-medium" : "text-text-secondary active:bg-bg-secondary",
      )}
    >
      <Icon className={cn("h-[18px] w-[18px]", aktiv ? "text-accent" : "text-text-tertiary")} aria-hidden />
      {e.label}
    </Link>
  );
}

function Blatt({ offen, setOffen, club, user }: { offen: boolean; setOffen: (o: boolean) => void; club: ClubView; user: UserView }) {
  const pathname = usePathname();
  return (
    <Dialog.Root open={offen} onOpenChange={setOffen}>
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade-in fixed inset-0 z-50 bg-black/30 lg:hidden" />
        <Dialog.Content
          aria-describedby={undefined}
          className="bg-bg-elevated shadow-modal fixed inset-y-0 left-0 z-50 flex w-[min(320px,86vw)] flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] lg:hidden"
        >
          <div className="flex h-14 shrink-0 items-center gap-2.5 px-4">
            <ClubhubMark />
            <Dialog.Title className="text-heading-sm text-text-primary flex-1 truncate">clubhub</Dialog.Title>
            <Dialog.Close className="text-text-tertiary grid h-9 w-9 place-items-center rounded-lg" aria-label="Schließen">
              <X className="h-5 w-5" aria-hidden />
            </Dialog.Close>
          </div>
          <p className="text-caption text-text-tertiary px-4 pb-2">{club.name}</p>
          <nav className="flex-1 space-y-4 overflow-y-auto px-2 pb-4" aria-label="Bereiche">
            <div className="space-y-0.5">
              {NAVIGATION.filter((e) => e.gruppe === null).map((e) => (
                <Zeile key={e.href} e={e} aktiv={istAktiv(pathname, e.href)} />
              ))}
            </div>
            {NAV_GRUPPEN.map((g) => (
              <div key={g} className="space-y-0.5">
                <p className="text-text-tertiary px-3 pb-1 text-[10px] font-medium tracking-[0.08em] uppercase">{g}</p>
                {NAVIGATION.filter((e) => e.gruppe === g).map((e) => (
                  <Zeile key={e.href} e={e} aktiv={istAktiv(pathname, e.href)} />
                ))}
              </div>
            ))}
            <div className="border-border-subtle space-y-0.5 border-t pt-3">
              {NAV_FUSS.map((e) => (
                <Zeile key={e.href} e={e} aktiv={istAktiv(pathname, e.href)} />
              ))}
            </div>
          </nav>
          <div className="border-border-subtle flex items-center gap-2 border-t px-2 py-2">
            {/* Der eigene Name fuehrt ins Konto — wie unten in der Leiste. */}
            <Link
              href={NAV_KONTO.href}
              aria-current={istAktiv(pathname, NAV_KONTO.href) ? "page" : undefined}
              className={cn(
                "flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-1.5",
                istAktiv(pathname, NAV_KONTO.href) ? "bg-bg-tertiary" : "active:bg-bg-secondary",
              )}
            >
              <div className="bg-accent-2-subtle text-accent-2 grid h-8 w-8 shrink-0 place-items-center rounded-full text-[11px] font-medium">
                {user.firstName[0]}
                {user.lastName[0]}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-body-sm text-text-primary truncate font-medium">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-caption text-text-tertiary truncate">{user.email}</p>
              </div>
            </Link>
            <ThemeSwitch />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Der Burger oben links — nur unterhalb von lg. */
export function MobileNav({ club, user }: { club: ClubView; user: UserView }) {
  const [offen, setOffen] = useState(false);
  const pathname = usePathname();
  useEffect(() => setOffen(false), [pathname]);
  return (
    <>
      <button
        type="button"
        onClick={() => setOffen(true)}
        aria-label="Navigation öffnen"
        className="text-text-secondary hover:bg-bg-secondary grid h-9 w-9 shrink-0 place-items-center rounded-lg lg:hidden"
      >
        <Menu className="h-5 w-5" aria-hidden />
      </button>
      <Blatt offen={offen} setOffen={setOffen} club={club} user={user} />
    </>
  );
}

/** Die Leiste unten — vier Hauptbereiche und „Mehr“. */
export function BottomNav({ club, user }: { club: ClubView; user: UserView }) {
  const pathname = usePathname();
  const [offen, setOffen] = useState(false);
  useEffect(() => setOffen(false), [pathname]);
  const haupt = NAVIGATION.filter((e) => e.mobil).slice(0, 4);
  return (
    <>
      <nav
        aria-label="Schnellnavigation"
        className="glass shadow-e2 fixed inset-x-0 bottom-0 z-40 flex items-stretch justify-around border-t border-black/[0.04] pb-[env(safe-area-inset-bottom)] lg:hidden dark:border-white/[0.06]"
      >
        {haupt.map((e) => {
          const aktiv = istAktiv(pathname, e.href);
          const Icon = e.icon;
          return (
            <Link
              key={e.href}
              href={e.href}
              aria-current={aktiv ? "page" : undefined}
              className={cn("flex min-w-0 flex-1 flex-col items-center gap-0.5 pt-2 pb-1.5", aktiv ? "text-accent" : "text-text-tertiary")}
            >
              <Icon className="h-5 w-5" aria-hidden />
              <span className="text-[10.5px] leading-tight font-medium">{e.label}</span>
            </Link>
          );
        })}
        <button
          type="button"
          onClick={() => setOffen(true)}
          className="text-text-tertiary flex min-w-0 flex-1 flex-col items-center gap-0.5 pt-2 pb-1.5"
        >
          <MoreHorizontal className="h-5 w-5" aria-hidden />
          <span className="text-[10.5px] leading-tight font-medium">Mehr</span>
        </button>
      </nav>
      <Blatt offen={offen} setOffen={setOffen} club={club} user={user} />
    </>
  );
}
